import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../axiosinstance";
import DeedPreviewRenderer from "./components/DeedPreviewRenderer";
import "./Deeds.css";

const DeedFinalPreviewPage = ({ isSidebarExpanded }) => {
  const { deed_instance_id } = useParams();
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState("");
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!deed_instance_id) {
      setPreviewLoading(false);
      setPreviewError("Missing deed instance.");
      return;
    }

    const fetchPreview = async () => {
      setPreviewLoading(true);
      setPreviewError("");
      try {
        const res = await axiosInstance.get(
          `/api/deed/preview/v4/${deed_instance_id}`
        );
        setPreviewData(res.data);
      } catch (error) {
        console.error("Error fetching final preview:", error);
        setPreviewError("Failed to load final preview.");
      } finally {
        setPreviewLoading(false);
      }
    };

    fetchPreview();
  }, [deed_instance_id]);

  const deedName = previewData?.deed?.name || "Deed";
  const deedTitle = useMemo(() => deedName.toUpperCase(), [deedName]);
  const previewItems = Array.isArray(previewData?.clauses) ? previewData.clauses : [];

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const handleDownloadWord = async () => {
    if (!deed_instance_id || downloadLoading) return;
    setDownloadLoading(true);
    setDownloadError("");
    setOpen(false);
    try {
      const response = await axiosInstance.get(
        `/api/deed/download/word/${deed_instance_id}`,
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], {
        type:
          response?.headers?.["content-type"] ||
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const objectUrl = window.URL.createObjectURL(blob);
      const safeDeedName = (deedName || "Deed").replace(/[\\/:*?"<>|]+/g, " ").trim();
      const fileName = `${safeDeedName || "Deed"}_${deed_instance_id}.docx`;
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Error downloading deed:", error);
      setDownloadError("Failed to download Word file. Please try again.");
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!deed_instance_id || downloadLoading) return;
    setDownloadLoading(true);
    setDownloadError("");
    setOpen(false);
    try {
      const response = await axiosInstance.get(
        `/api/deed/download/pdf/${deed_instance_id}`,
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], {
        type: response?.headers?.["content-type"] || "application/pdf",
      });
      const objectUrl = window.URL.createObjectURL(blob);
      const safeDeedName = (deedName || "Deed").replace(/[\\/:*?"<>|]+/g, " ").trim();
      const fileName = `${safeDeedName || "Deed"}_${deed_instance_id}.pdf`;
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Error downloading deed PDF:", error);
      setDownloadError("Failed to download PDF file. Please try again.");
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <div
      className={`deeds-container ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="deeds-main deeds-final-main">
        <div className="final-header">
          <div className="deeds-final-ready-text">Your document is ready</div>
          <div className="deeds-final-download-wrap" ref={dropdownRef}>
            <button
              className="btn btn-primary deeds-download-btn"
              type="button"
              onClick={() => setOpen((prev) => !prev)}
              disabled={downloadLoading || previewLoading || !!previewError}
              aria-haspopup="menu"
              aria-expanded={open}
            >
              {downloadLoading ? "Downloading..." : "Download"}
              <span className="deeds-download-chevron" aria-hidden="true">
                v
              </span>
            </button>
            {open && !downloadLoading && (
              <div className="deeds-download-menu download-menu" role="menu">
                <button
                  className="deeds-download-item"
                  type="button"
                  role="menuitem"
                  onClick={handleDownloadWord}
                >
                  Download as Word (.docx)
                </button>
                <button
                  className="deeds-download-item"
                  type="button"
                  role="menuitem"
                  onClick={handleDownloadPdf}
                >
                  Download as PDF (.pdf)
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="final-body">
          {downloadError && (
            <div className="deeds-error">
              <span>{downloadError}</span>
            </div>
          )}

          {previewLoading && <div className="deeds-muted">Loading preview...</div>}

          {!previewLoading && previewError && (
            <div className="deeds-error">
              <span>{previewError}</span>
            </div>
          )}

          {!previewLoading && !previewError && (
            <div className="deeds-final-preview-shell">
              <div className="deeds-fill-panel deeds-fill-preview deeds-final-preview-card">
                <DeedPreviewRenderer
                  deedTitle={deedTitle}
                  previewItems={previewItems}
                  containerClassName="deeds-preview-scroll deeds-final-preview-scroll"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeedFinalPreviewPage;
